(() => {
  if (customElements.get("creator-program-popup")) {
    return;
  }

  const KEYS = Object.freeze({
    session: "sothing_creator_popup_session_v1",
    dismissed: "sothing_creator_popup_dismissed_v1",
    converted: "sothing_creator_popup_converted_v1",
  });

  const HOUR = 60 * 60 * 1000;
  const DAY = 24 * HOUR;

  class SafeStore {
    constructor(type) {
      this.type = type;
      this.storage = null;

      try {
        this.storage = window[type];
        const probe = `sothing_creator_popup_probe_${type}`;
        this.storage.setItem(probe, "1");
        this.storage.removeItem(probe);
      } catch (_error) {
        this.storage = null;
      }
    }

    get(key) {
      if (this.storage) {
        try {
          return this.storage.getItem(key);
        } catch (_error) {
          this.storage = null;
        }
      }

      const prefix = `${encodeURIComponent(key)}=`;
      const match = document.cookie
        .split(";")
        .map((item) => item.trim())
        .find((item) => item.startsWith(prefix));

      if (!match) {
        return null;
      }

      try {
        return decodeURIComponent(match.slice(prefix.length));
      } catch (_error) {
        this.remove(key);
        return null;
      }
    }

    set(key, value, maxAge = null) {
      if (this.storage) {
        try {
          this.storage.setItem(key, value);
          return true;
        } catch (_error) {
          this.storage = null;
        }
      }

      try {
        const age = Number.isFinite(maxAge) ? `; Max-Age=${Math.max(0, Math.round(maxAge))}` : "";
        document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${age}`;
        return document.cookie.includes(`${encodeURIComponent(key)}=`);
      } catch (_error) {
        return false;
      }
    }

    remove(key) {
      if (this.storage) {
        try {
          this.storage.removeItem(key);
        } catch (_error) {
          this.storage = null;
        }
      }

      try {
        document.cookie = `${encodeURIComponent(key)}=; Path=/; SameSite=Lax; Max-Age=0`;
      } catch (_error) {
        return;
      }
    }
  }

  class CreatorProgramPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.abortController = new AbortController();
      this.modal = this.querySelector("[data-creator-program-modal]");
      this.conversionLink = this.querySelector("[data-creator-popup-conversion]");
      this.sessionStore = new SafeStore("sessionStorage");
      this.localStore = new SafeStore("localStorage");
      this.triggerDelay = this.numberFromDataset("triggerDelay", 5);
      this.minimumDismissHours = this.numberFromDataset("minimumDismissHours", 8);
      this.conversionSuppressionDays = this.numberFromDataset("conversionSuppressionDays", 7);
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.hasOpened = false;
      this.converted = false;
      this.allowClose = false;
      this.memorySessionState = null;

      if (!this.modal) {
        return;
      }

      const signal = this.abortController.signal;
      this.addEventListener("click", (event) => this.onClick(event), { capture: true, signal });
      document.addEventListener("keydown", (event) => this.onKeydown(event), { capture: true, signal });
      this.modal.addEventListener("openable-element:open", () => this.onOpen(), { signal });
      this.modal.addEventListener("openable-element:close", () => this.onClose(), { signal });
      window.addEventListener("pageshow", (event) => this.onPageShow(event), { signal });

      if (this.designMode) {
        if (this.conversionLink) {
          this.conversionLink.addEventListener("click", (event) => event.preventDefault(), { signal });
        }
        return;
      }

      this.scheduleOpen(this.triggerDelay * 1000);
    }

    disconnectedCallback() {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.initialized = false;
    }

    numberFromDataset(name, fallback) {
      const value = Number(this.dataset[name]);
      return Number.isFinite(value) && value >= 0 ? value : fallback;
    }

    isEligible() {
      return true;
    }

    scheduleOpen(delay = 0) {
      if (this.timer) {
        window.clearTimeout(this.timer);
      }

      this.timer = window.setTimeout(() => this.attemptOpen(), delay);
    }

    attemptOpen() {
      this.timer = null;

      if (!this.isConnected || this.hasOpened) {
        return;
      }

      if (this.hasOpenConflict()) {
        this.scheduleOpen(250);
        return;
      }

      this.hasOpened = true;
      this.modal.open = true;
    }

    onOpen() {
      this.hasOpened = true;
    }

    onClose() {
      if (this.designMode || this.allowClose) {
        this.allowClose = false;
        return;
      }

      window.requestAnimationFrame(() => {
        if (this.isConnected && !this.modal.open) {
          this.modal.open = true;
        }
      });
    }

    onClick(event) {
      const closeButton = event.target.closest('[data-creator-popup-dismiss="button"]');
      if (closeButton && this.contains(closeButton)) {
        this.allowClose = true;
      }

      const conversion = event.target.closest("[data-creator-popup-conversion]");
      if (conversion && this.contains(conversion)) {
        if (this.designMode) {
          event.preventDefault();
          return;
        }

        this.converted = true;
      }
    }

    onKeydown(event) {
      if (event.key === "Escape" && this.modal.open && !this.designMode) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    onPageShow(event) {
      if (!event.persisted || this.designMode) {
        return;
      }

      if (this.modal.open) {
        this.hasOpened = true;
        return;
      }

      this.hasOpened = false;
      this.allowClose = false;
      this.scheduleOpen();
    }

    getSessionState() {
      return this.sessionStore.get(KEYS.session) || this.memorySessionState;
    }

    setSessionState(value) {
      this.memorySessionState = value;
      this.sessionStore.set(KEYS.session, value);
    }

    readTimestamp(key) {
      const raw = this.localStore.get(key);
      if (!raw) {
        return null;
      }

      const value = Number(raw);
      if (!Number.isFinite(value) || value <= 0 || value > Date.now() + 5 * 60 * 1000) {
        this.localStore.remove(key);
        return null;
      }

      return value;
    }

    readDismissal() {
      const raw = this.localStore.get(KEYS.dismissed);
      if (!raw) {
        return null;
      }

      try {
        const value = JSON.parse(raw);
        const validDate = /^\d{4}-\d{2}-\d{2}$/.test(value.date);
        const validTimestamp = Number.isFinite(value.timestamp) && value.timestamp > 0 && value.timestamp <= Date.now() + 5 * 60 * 1000;

        if (!validDate || !validTimestamp) {
          throw new Error("Invalid dismissal state");
        }

        return value;
      } catch (_error) {
        this.localStore.remove(KEYS.dismissed);
        return null;
      }
    }

    localDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    hasOpenConflict() {
      if (document.documentElement.classList.contains("lock-all") || document.documentElement.classList.contains("lock-mobile")) {
        return true;
      }

      const selectors = [
        "modal-content[open]",
        "drawer-content[open]",
        "cart-drawer[open]",
        "quick-buy-drawer[open]",
        "quick-buy-popover[open]",
        "popover-content[open]",
        "mobile-navigation[open]",
        '[role="dialog"][aria-modal="true"]',
        "shopify-forms",
      ];

      const roots = [document];
      const visited = new Set();

      while (roots.length) {
        const root = roots.shift();
        if (!root || visited.has(root)) {
          continue;
        }

        visited.add(root);

        for (const selector of selectors) {
          const elements = root.querySelectorAll(selector);
          for (const element of elements) {
            if (element === this.modal || this.contains(element)) {
              continue;
            }

            if (this.isVisible(element)) {
              return true;
            }
          }
        }

        for (const element of root.querySelectorAll("*")) {
          if (element.shadowRoot) {
            roots.push(element.shadowRoot);
          }
        }
      }

      return false;
    }

    isVisible(element) {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return style.display !== "none" && style.visibility !== "hidden" && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0;
    }
  }

  customElements.define("creator-program-popup", CreatorProgramPopup);
})();
